import { useQuery } from '@tanstack/react-query'
import {
  Box, Typography, Chip, CircularProgress, Paper,
  Table, TableBody, TableCell, TableHead, TableRow, Link, Tooltip,
} from '@mui/material'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { getGuardLog } from '../../api/client'
import type { WorkItemGuardAction } from '../../api/types'

const ACTION_CONFIG: Record<WorkItemGuardAction['action'], {
  label: string
  color: 'warning' | 'success' | 'error'
  icon: React.ReactElement
}> = {
  Reopened: {
    label: 'Reopened',
    color: 'warning',
    icon: <AutorenewIcon sx={{ fontSize: 14 }} />,
  },
  LeftClosed: {
    label: 'Left Closed',
    color: 'success',
    icon: <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />,
  },
  Error: {
    label: 'Error',
    color: 'error',
    icon: <ErrorOutlineIcon sx={{ fontSize: 14 }} />,
  },
}

const TYPE_COLOR: Record<string, string> = {
  'User Story': '#1a56db',
  'Bug': '#c0392b',
  'Vulnerability': '#8e44ad',
}

export default function GuardLogPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['guard-log'],
    queryFn: () => getGuardLog(50),
    refetchInterval: 5_000,
  })

  const actions = data?.data ?? []

  return (
    <Box>
      <Box mb={2}>
        <Typography variant="h5" fontWeight={600}>Work Item Guard</Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Automatically reopens User Stories, Bugs, and Vulnerabilities closed without linked PRs or commits.
          Refreshes every 5s.
        </Typography>
      </Box>

      {isLoading ? (
        <CircularProgress />
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: 'grey.50' } }}>
                <TableCell>When</TableCell>
                <TableCell>Work Item</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>State transition</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {actions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                    No guard events yet — configure the ADO service hook to start seeing activity here.
                  </TableCell>
                </TableRow>
              ) : actions.map(a => {
                const cfg = ACTION_CONFIG[a.action] ?? ACTION_CONFIG.Error
                return (
                  <TableRow key={a.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: 12 }}>
                      {new Date(a.evaluatedAt).toLocaleString()}
                    </TableCell>

                    <TableCell>
                      {a.workItemUrl ? (
                        <Link href={a.workItemUrl} target="_blank" rel="noopener"
                          sx={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600 }}>
                          #{a.workItemId}
                        </Link>
                      ) : (
                        <Typography fontFamily="monospace" fontSize={13} fontWeight={600}>
                          #{a.workItemId}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <Typography fontSize={12} fontWeight={500}
                        sx={{ color: TYPE_COLOR[a.workItemType] ?? 'text.primary' }}>
                        {a.workItemType}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                      {a.project}
                    </TableCell>

                    <TableCell>
                      <Chip
                        icon={cfg.icon}
                        label={cfg.label}
                        color={cfg.color}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>

                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {a.oldState} → Closed
                    </TableCell>

                    <TableCell sx={{ maxWidth: 300 }}>
                      <Tooltip title={a.details} placement="top-start">
                        <Typography fontSize={12} color="text.secondary"
                          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {a.details}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  )
}
